import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Shield, Users, RefreshCw, Save, AlertTriangle, Search,
  LayoutDashboard, Package, ArrowLeftRight, FileText, Calendar,
  Settings, Database, Mail, Palette, ClipboardCheck, Bug, Handshake, Radar, QrCode,
  Wrench, CalendarDays, Copy, Lock
} from 'lucide-react';

const ROLE_LABELS = {
  superadmin: 'Super Admin',
  admin: 'Administrador',
  supervisor: 'Supervisor',
  auditor: 'Auditor',
  almacenista: 'Almacenista',
  user: 'Usuario'
};

// Complete permission groups matching backend - ULTRA GRANULAR
const PERMISSION_GROUPS = {
  'Dashboard': {
    icon: LayoutDashboard,
    permissions: [
      { key: 'view_dashboard', label: 'Ver Dashboard' },
      { key: 'view_dashboard_kpis', label: 'Ver KPIs' },
      { key: 'view_dashboard_charts', label: 'Ver Gráficas' },
      { key: 'export_dashboard', label: 'Exportar Dashboard' },
      { key: 'customize_dashboard', label: 'Personalizar Dashboard' }
    ]
  },
  'Operaciones': {
    icon: Radar,
    permissions: [
      { key: 'view_operations', label: 'Ver Panel de Operaciones' }
    ]
  },
  'Inventario': {
    icon: Package,
    permissions: [
      { key: 'view_inventory', label: 'Ver Lista de Inventario' },
      { key: 'view_inventory_detail', label: 'Ver Detalle de Equipo' },
      { key: 'create_inventory', label: 'Crear Equipo' },
      { key: 'edit_inventory', label: 'Editar Equipo' },
      { key: 'delete_inventory', label: 'Eliminar Equipo' },
      { key: 'restore_inventory', label: 'Restaurar Equipo Eliminado' },
      { key: 'bulk_delete_inventory', label: 'Eliminación Masiva' },
      { key: 'change_inventory_status', label: 'Cambiar Estado' },
      { key: 'view_inventory_history', label: 'Ver Historial de Condiciones' },
      { key: 'import_inventory', label: 'Importar Inventario' },
      { key: 'export_inventory', label: 'Exportar Inventario' },
      { key: 'reconcile_inventory', label: 'Reconciliar Stock' }
    ]
  },
  'Categorías': {
    icon: Database,
    permissions: [
      { key: 'view_categories', label: 'Ver Categorías' },
      { key: 'create_categories', label: 'Crear Categorías' },
      { key: 'edit_categories', label: 'Editar Categorías' },
      { key: 'delete_categories', label: 'Eliminar Categorías' },
      { key: 'bulk_delete_categories', label: 'Eliminación Masiva de Categorías' }
    ]
  },
  'Ubicaciones': {
    icon: Database,
    permissions: [
      { key: 'view_locations', label: 'Ver Ubicaciones' },
      { key: 'create_locations', label: 'Crear Ubicaciones' },
      { key: 'edit_locations', label: 'Editar Ubicaciones' },
      { key: 'delete_locations', label: 'Eliminar Ubicaciones' },
      { key: 'bulk_delete_locations', label: 'Eliminación Masiva de Ubicaciones' }
    ]
  },
  'Responsables': {
    icon: Users,
    permissions: [
      { key: 'view_responsibles', label: 'Ver Responsables' },
      { key: 'create_responsibles', label: 'Crear Responsables' },
      { key: 'edit_responsibles', label: 'Editar Responsables' },
      { key: 'delete_responsibles', label: 'Eliminar Responsables' },
      { key: 'reset_responsible_password', label: 'Resetear Contraseña' },
      { key: 'bulk_delete_responsibles', label: 'Eliminación Masiva de Responsables' }
    ]
  },
  'Movimientos': {
    icon: ArrowLeftRight,
    permissions: [
      { key: 'view_movements', label: 'Ver Lista de Movimientos' },
      { key: 'view_movement_detail', label: 'Ver Detalle de Movimiento' },
      { key: 'create_event_checkout', label: 'Crear Salida (Evento)' },
      { key: 'create_loan_checkout', label: 'Crear Préstamo' },
      { key: 'create_return', label: 'Registrar Retorno' },
      { key: 'checkout_without_review', label: 'Salida SIN Revisión (Urgencia)' },
      { key: 'use_emergency_mode', label: 'Usar Modo Emergencia' },
      { key: 'edit_movements', label: 'Editar Notas de Movimiento' },
      { key: 'delete_movements', label: 'Eliminar Movimientos' },
      { key: 'approve_movements', label: 'Aprobar como Override Admin' },
      { key: 'restore_movements', label: 'Restaurar Movimientos' },
      { key: 'download_movement_pdf', label: 'Descargar Responsiva PDF' },
      { key: 'duplicate_movements', label: 'Duplicar Movimientos' },
      { key: 'use_barcode_scanner', label: 'Usar Escaner QR/Codigo de Barras' }
    ]
  },
  'Rastreo de Equipo': {
    icon: Radar,
    permissions: [
      { key: 'view_equipment_tracking', label: 'Ver Página de Rastreo' },
      { key: 'view_equipment_at_events', label: 'Ver Equipo en Eventos' },
      { key: 'view_equipment_on_loan', label: 'Ver Equipo en Préstamo' },
      { key: 'view_overdue_equipment', label: 'Ver Equipo Vencido' },
      { key: 'configure_loan_alerts', label: 'Configurar Alertas' },
      { key: 'manage_loan_reminders', label: 'Gestionar Recordatorios' }
    ]
  },
  'Calendario': {
    icon: Calendar,
    permissions: [
      { key: 'view_calendar', label: 'Ver Calendario' },
      { key: 'create_calendar_events', label: 'Crear Eventos' },
      { key: 'edit_calendar', label: 'Editar Eventos' },
      { key: 'delete_calendar_events', label: 'Eliminar Eventos' }
    ]
  },
  'Reportes': {
    icon: FileText,
    permissions: [
      { key: 'view_reports', label: 'Ver Reportes' },
      { key: 'generate_reports', label: 'Generar Reportes' },
      { key: 'export_reports_excel', label: 'Exportar a Excel' },
      { key: 'export_reports_pdf', label: 'Exportar a PDF' }
    ]
  },
  'Usuarios': {
    icon: Users,
    permissions: [
      { key: 'view_users', label: 'Ver Lista de Usuarios' },
      { key: 'create_users', label: 'Crear Usuarios' },
      { key: 'edit_users', label: 'Editar Usuarios' },
      { key: 'delete_users', label: 'Eliminar Usuarios' },
      { key: 'change_user_role', label: 'Cambiar Rol de Usuario' },
      { key: 'manage_user_permissions', label: 'Asignar Permisos Individuales' },
      { key: 'bulk_delete_users', label: 'Eliminación Masiva de Usuarios' }
    ]
  },
  'Permisos de Roles': {
    icon: Shield,
    permissions: [
      { key: 'manage_permissions', label: 'Gestionar Permisos' },
      { key: 'view_role_permissions', label: 'Ver Permisos por Rol' },
      { key: 'edit_role_permissions', label: 'Editar Permisos de Roles' },
      { key: 'create_custom_roles', label: 'Crear Roles Personalizados' }
    ]
  },
  'Auditoría': {
    icon: FileText,
    permissions: [
      { key: 'view_audit', label: 'Ver Auditoría' },
      { key: 'export_audit', label: 'Exportar Auditoría' }
    ]
  },
  'Debugger': {
    icon: Bug,
    permissions: [
      { key: 'view_debugger', label: 'Ver Debugger' },
      { key: 'manage_system_logs', label: 'Gestionar Logs del Sistema' }
    ]
  },
  'Backup': {
    icon: Database,
    permissions: [
      { key: 'view_backup', label: 'Ver Backups' },
      { key: 'create_backup', label: 'Crear Backup' },
      { key: 'restore_backup', label: 'Restaurar Backup' },
      { key: 'manage_onedrive', label: 'Gestionar OneDrive' }
    ]
  },
  'Correos': {
    icon: Mail,
    permissions: [
      { key: 'view_email_config', label: 'Ver Configuración de Correo' },
      { key: 'send_emails', label: 'Enviar Correos' }
    ]
  },
  'Anuncios': {
    icon: Mail,
    permissions: [
      { key: 'view_announcements', label: 'Ver Anuncios' },
      { key: 'create_announcements', label: 'Crear Anuncios' },
      { key: 'edit_announcements', label: 'Editar Anuncios' },
      { key: 'delete_announcements', label: 'Eliminar Anuncios' },
      { key: 'manage_announcements', label: 'Gestión Completa de Anuncios' }
    ]
  },
  'Estilo': {
    icon: Palette,
    permissions: [
      { key: 'view_style', label: 'Ver Configuración de Estilo' },
      { key: 'edit_style_colors', label: 'Editar Colores' },
      { key: 'edit_style_typography', label: 'Editar Tipografía' },
      { key: 'edit_style_login', label: 'Editar Página de Login' },
      { key: 'manage_style', label: 'Gestión Completa de Estilo' }
    ]
  },
  'Plantillas': {
    icon: FileText,
    permissions: [
      { key: 'view_templates', label: 'Ver Plantillas' },
      { key: 'edit_templates', label: 'Editar Plantillas' }
    ]
  },
  'Responsivas': {
    icon: Handshake,
    permissions: [
      { key: 'view_responsivas', label: 'Ver Responsivas' },
      { key: 'download_responsivas', label: 'Descargar Responsivas' },
      { key: 'regenerate_responsivas', label: 'Regenerar PDF' },
      { key: 'delete_responsivas', label: 'Eliminar Responsivas' }
    ]
  },
  'Exportación/Importación': {
    icon: FileText,
    permissions: [
      { key: 'export_data', label: 'Exportar Datos' },
      { key: 'import_data', label: 'Importar Datos' }
    ]
  },
  'Checklists': {
    icon: ClipboardCheck,
    permissions: [
      { key: 'view_checklists', label: 'Ver Checklists' },
      { key: 'manage_checklists', label: 'Gestionar Checklists' }
    ]
  },
  'Etiquetas QR/Códigos': {
    icon: QrCode,
    permissions: [
      { key: 'view_etiquetas', label: 'Ver menú Etiquetas QR' },
      { key: 'generate_etiquetas', label: 'Generar códigos QR/Barras' },
      { key: 'print_etiquetas', label: 'Imprimir etiquetas' },
      { key: 'use_barcode_scanner', label: 'Usar escáner en Movimientos' },
      { key: 'configure_barcode_scanner', label: 'Configurar escáner' }
    ]
  },
  'Mantenimiento': {
    icon: Wrench,
    permissions: [
      { key: 'view_maintenance', label: 'Ver Mantenimiento' },
      { key: 'create_maintenance', label: 'Crear Registro de Mantenimiento' },
      { key: 'edit_maintenance', label: 'Editar Mantenimiento' },
      { key: 'delete_maintenance', label: 'Eliminar Mantenimiento' }
    ]
  },
  'Reservas': {
    icon: CalendarDays,
    permissions: [
      { key: 'view_reservations', label: 'Ver Reservas' },
      { key: 'create_reservations', label: 'Crear Reservas' },
      { key: 'edit_reservations', label: 'Editar Reservas' },
      { key: 'delete_reservations', label: 'Eliminar Reservas' }
    ]
  },
  'Clientes': {
    icon: Users,
    permissions: [
      { key: 'view_clients', label: 'Ver Clientes' },
      { key: 'create_clients', label: 'Crear Clientes' },
      { key: 'edit_clients', label: 'Editar Clientes' },
      { key: 'delete_clients', label: 'Eliminar Clientes' }
    ]
  }
};

// Flatten all permissions for search
const ALL_PERMISSIONS = Object.entries(PERMISSION_GROUPS).flatMap(([group, { permissions }]) =>
  permissions.map(p => ({ ...p, group }))
);

const PermissionsPage = () => {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('roles');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(
    Object.keys(PERMISSION_GROUPS).reduce((acc, key) => ({ ...acc, [key]: false }), {})
  );
  
  // Role-level collapse state (all collapsed by default)
  const [expandedRoles, setExpandedRoles] = useState({});
  
  // Role permissions state
  const [rolePermissions, setRolePermissions] = useState({});
  const [modifiedRoles, setModifiedRoles] = useState({});
  
  // User permissions state
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);
  const [selectedUserCustom, setSelectedUserCustom] = useState(false);
  const [userModified, setUserModified] = useState(false);

  // Special permissions settings
  const [specialSettings, setSpecialSettings] = useState({});
  const [specialSettingsModified, setSpecialSettingsModified] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const apiInstance = api();
      const [roleRes, usersRes] = await Promise.all([
        apiInstance.get('/role-permissions'),
        apiInstance.get('/users')
      ]);
      
      setRolePermissions(roleRes.data || {});
      setUsers(usersRes.data || []);

      // Load special permissions settings
      try {
        const spRes = await apiInstance.get('/special-permissions-settings');
        setSpecialSettings(spRes.data?.operations || {});
      } catch (e) { /* ignore */ }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter permissions by search
  const filteredGroups = searchTerm
    ? Object.entries(PERMISSION_GROUPS).reduce((acc, [group, data]) => {
        const filtered = data.permissions.filter(p =>
          p.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.key.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filtered.length > 0) {
          acc[group] = { ...data, permissions: filtered };
        }
        return acc;
      }, {})
    : PERMISSION_GROUPS;

  // Toggle group expansion
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Expand/collapse all
  const toggleAllGroups = (expand) => {
    setExpandedGroups(
      Object.keys(PERMISSION_GROUPS).reduce((acc, key) => ({ ...acc, [key]: expand }), {})
    );
  };

  // Handle role permission toggle
  const toggleRolePermission = (role, permission) => {
    const current = modifiedRoles[role] || rolePermissions[role] || [];
    const updated = current.includes(permission)
      ? current.filter(p => p !== permission)
      : [...current, permission];
    
    setModifiedRoles(prev => ({ ...prev, [role]: updated }));
  };

  // Select/deselect all permissions in a group for a role
  const toggleGroupPermissions = (role, groupPermissions, selectAll) => {
    const current = modifiedRoles[role] || rolePermissions[role] || [];
    const permKeys = groupPermissions.map(p => p.key);
    
    const updated = selectAll
      ? [...new Set([...current, ...permKeys])]
      : current.filter(p => !permKeys.includes(p));
    
    setModifiedRoles(prev => ({ ...prev, [role]: updated }));
  };

  // Save role permissions
  const saveRolePermissions = async (role) => {
    try {
      setSaving(true);
      const permissions = modifiedRoles[role] || rolePermissions[role] || [];
      await api().put(`/role-permissions/${role}`, { role, permissions });
      
      setRolePermissions(prev => ({ ...prev, [role]: permissions }));
      setModifiedRoles(prev => {
        const next = { ...prev };
        delete next[role];
        return next;
      });
      
      toast.success(`Permisos de ${ROLE_LABELS[role]} actualizados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Reset role to defaults
  const resetRolePermissions = async (role) => {
    try {
      setSaving(true);
      const res = await api().post(`/role-permissions/${role}/reset`);
      
      setRolePermissions(prev => ({ ...prev, [role]: res.data.permissions }));
      setModifiedRoles(prev => {
        const next = { ...prev };
        delete next[role];
        return next;
      });
      
      toast.success(`Permisos de ${ROLE_LABELS[role]} restablecidos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer');
    } finally {
      setSaving(false);
    }
  };

  // Load user permissions when selected
  useEffect(() => {
    if (selectedUserId) {
      const selectedUser = users.find(u => u.id === selectedUserId);
      if (selectedUser) {
        setSelectedUserPermissions(selectedUser.permissions || []);
        setSelectedUserCustom(selectedUser.use_custom_permissions || false);
        setUserModified(false);
      }
    }
  }, [selectedUserId, users]);

  // Toggle user custom permissions
  const toggleUserCustom = () => {
    setSelectedUserCustom(prev => !prev);
    setUserModified(true);
  };

  // Toggle individual user permission
  const toggleUserPermission = (permission) => {
    setSelectedUserPermissions(prev => 
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
    setUserModified(true);
  };

  // Save user permissions
  const saveUserPermissions = async () => {
    if (!selectedUserId) return;
    
    try {
      setSaving(true);
      await api().put(`/users/${selectedUserId}`, {
        permissions: selectedUserCustom ? selectedUserPermissions : null,
        use_custom_permissions: selectedUserCustom
      });
      
      setUsers(prev => prev.map(u => 
        u.id === selectedUserId 
          ? { ...u, permissions: selectedUserPermissions, use_custom_permissions: selectedUserCustom }
          : u
      ));
      
      setUserModified(false);
      toast.success('Permisos de usuario actualizados');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const getRolePermissions = (role) => modifiedRoles[role] || rolePermissions[role] || [];
  const hasRoleModifications = (role) => !!modifiedRoles[role];

  const saveDuplicateSettings = async () => {
    try {
      setSaving(true);
      await api().put('/special-permissions-settings', { operations: specialSettings });
      setSpecialSettingsModified(false);
      toast.success('Permisos especiales actualizados');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Count permissions in a group for a role
  const countGroupPermissions = (role, permissions) => {
    const current = getRolePermissions(role);
    const active = permissions.filter(p => current.includes(p.key)).length;
    return { active, total: permissions.length };
  };

  // Render permission checkbox
  const PermissionCheckbox = ({ permission, isActive, onToggle, disabled = false }) => (
    <label
      className={`flex items-center gap-2 px-3 py-2.5 rounded-md border cursor-pointer transition-all min-h-[44px] ${
        isActive 
          ? 'bg-primary/10 border-primary text-primary' 
          : 'bg-muted/30 hover:bg-muted/50 border-transparent'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Checkbox
        checked={isActive}
        onCheckedChange={onToggle}
        disabled={disabled}
        className="h-5 w-5"
      />
      <span className="text-sm select-none">{permission.label}</span>
    </label>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Gestión de Permisos
          </h1>
          <p className="text-muted-foreground">Configura permisos por rol o por usuario individual</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar permisos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Por Rol
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Por Usuario
          </TabsTrigger>
        </TabsList>

        {/* Permisos por Rol */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Permisos por Rol</CardTitle>
                  <CardDescription>
                    Selecciona un rol y modifica sus permisos
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="admin">
                <TabsList className="flex flex-wrap h-auto gap-1 mb-4" data-testid="role-tabs">
                  {Object.entries(ROLE_LABELS).filter(([role]) => role !== 'superadmin').map(([role, label]) => (
                    <TabsTrigger key={role} value={role} className="flex items-center gap-1" data-testid={`role-tab-${role}`}>
                      {label}
                      <Badge variant="outline" className="ml-1 text-xs">
                        {getRolePermissions(role).length}
                      </Badge>
                      {hasRoleModifications(role) && (
                        <span className="w-2 h-2 rounded-full bg-yellow-500" />
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {Object.entries(ROLE_LABELS).filter(([role]) => role !== 'superadmin').map(([role, label]) => (
                  <TabsContent key={role} value={role}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={role === 'admin' ? 'default' : 'secondary'}>{label}</Badge>
                        <span className="text-sm text-muted-foreground">
                          ({getRolePermissions(role).length} permisos activos)
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => toggleAllGroups(true)}>Expandir Todo</Button>
                        <Button variant="outline" size="sm" onClick={() => toggleAllGroups(false)}>Colapsar Todo</Button>
                        <Button variant="outline" size="sm" onClick={() => resetRolePermissions(role)} disabled={saving}>
                          <RefreshCw className="h-4 w-4 mr-1" />Restablecer
                        </Button>
                        <Button size="sm" onClick={() => saveRolePermissions(role)} disabled={saving || !hasRoleModifications(role)} data-testid={`save-role-${role}`}>
                          <Save className="h-4 w-4 mr-1" />Guardar
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(filteredGroups).map(([group, { icon: Icon, permissions }]) => {
                        const { active, total } = countGroupPermissions(role, permissions);
                        const isExpanded = expandedGroups[group];
                        const allSelected = active === total;
                        
                        return (
                          <div key={group} className="border rounded-lg overflow-hidden">
                            <div 
                              className="flex items-center justify-between p-3 bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors min-h-[48px] gap-2"
                              onClick={() => toggleGroup(group)}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium text-sm truncate">{group}</span>
                                <Badge variant="outline" className={`flex-shrink-0 ${active === total ? 'bg-green-100 text-green-700' : active > 0 ? 'bg-yellow-100 text-yellow-700' : ''}`}>
                                  {active}/{total}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleGroupPermissions(role, permissions, !allSelected); }} className="h-8 text-xs px-2">
                                  {allSelected ? 'Quitar todos' : 'Seleccionar todos'}
                                </Button>
                                <span className="text-muted-foreground text-xs">{isExpanded ? '▼' : '▶'}</span>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="p-3 flex flex-wrap gap-2">
                                {permissions.map(perm => (
                                  <PermissionCheckbox
                                    key={perm.key}
                                    permission={perm}
                                    isActive={getRolePermissions(role).includes(perm.key)}
                                    onToggle={() => toggleRolePermission(role, perm.key)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permisos por Usuario */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Permisos por Usuario</CardTitle>
              <CardDescription>
                Asigna permisos personalizados a usuarios específicos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger data-testid="user-select">
                      <SelectValue placeholder="Selecciona un usuario" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.username}) - {ROLE_LABELS[u.role] || u.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedUserId && (
                  <Button
                    onClick={saveUserPermissions}
                    disabled={saving || !userModified}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Guardar Cambios
                  </Button>
                )}
              </div>

              {selectedUserId && (
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedUserCustom}
                        onCheckedChange={toggleUserCustom}
                      />
                      <span className="font-medium">Usar permisos personalizados</span>
                    </label>
                    {!selectedUserCustom && (
                      <Badge variant="outline">
                        Usando permisos del rol
                      </Badge>
                    )}
                  </div>

                  {selectedUserCustom && (
                    <>
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm text-yellow-700 dark:text-yellow-400">
                          Los permisos personalizados sobreescriben los del rol
                        </span>
                      </div>

                      {/* Special Permissions Quick Access */}
                      <div className="border rounded-lg overflow-hidden border-primary/30" data-testid="user-special-permissions">
                        <div className="p-3 bg-primary/10 flex items-center gap-2">
                          <Shield className="h-4 w-4 text-primary" />
                          <span className="font-medium text-sm">Permisos Especiales</span>
                          <Badge variant="outline" className="text-xs">
                            {[
                              'duplicate_movements', 'use_emergency_mode', 'use_barcode_scanner',
                              'checkout_without_review', 'approve_movements', 'bulk_delete_inventory',
                              'delete_movements', 'restore_movements'
                            ].filter(p => selectedUserPermissions.includes(p)).length}/8
                          </Badge>
                        </div>
                        <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                          {[
                            { key: 'approve_movements', label: 'Override Admin', desc: 'Aprobar movimientos como admin override' },
                            { key: 'duplicate_movements', label: 'Duplicar Movimientos', desc: 'Copiar un movimiento existente' },
                            { key: 'use_emergency_mode', label: 'Modo Emergencia', desc: 'Salidas sin verificacion de responsable' },
                            { key: 'use_barcode_scanner', label: 'Uso de QR / Scanner', desc: 'Escaner de codigos QR y barras' },
                            { key: 'checkout_without_review', label: 'Salida sin Revision', desc: 'Omitir revision en salidas urgentes' },
                            { key: 'bulk_delete_inventory', label: 'Eliminacion Masiva', desc: 'Eliminar multiples equipos' },
                            { key: 'delete_movements', label: 'Eliminar Movimientos', desc: 'Borrar del historial' },
                            { key: 'restore_movements', label: 'Restaurar Movimientos', desc: 'Recuperar eliminados' },
                          ].map(op => (
                            <label
                              key={op.key}
                              className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                                selectedUserPermissions.includes(op.key)
                                  ? 'bg-primary/10 border border-primary/30'
                                  : 'bg-muted/20 hover:bg-muted/40 border border-transparent'
                              }`}
                              data-testid={`user-special-${op.key}`}
                            >
                              <Checkbox
                                checked={selectedUserPermissions.includes(op.key)}
                                onCheckedChange={() => toggleUserPermission(op.key)}
                                className="h-4 w-4"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium leading-tight">{op.label}</p>
                                <p className="text-xs text-muted-foreground leading-tight">{op.desc}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {Object.entries(filteredGroups).map(([group, { icon: Icon, permissions }]) => {
                          const active = permissions.filter(p => selectedUserPermissions.includes(p.key)).length;
                          const isExpanded = expandedGroups[group];
                          
                          return (
                            <div key={group} className="border rounded-lg overflow-hidden">
                              <div 
                                className="flex items-center justify-between p-3 bg-muted/20 cursor-pointer hover:bg-muted/40 min-h-[48px] gap-2"
                                onClick={() => toggleGroup(group)}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  <span className="font-medium text-sm truncate">{group}</span>
                                  <Badge variant="outline" className="flex-shrink-0">{active}/{permissions.length}</Badge>
                                </div>
                                <span className="text-muted-foreground text-xs flex-shrink-0">{isExpanded ? '▼' : '▶'}</span>
                              </div>
                              
                              {isExpanded && (
                                <div className="p-3 flex flex-wrap gap-2">
                                  {permissions.map(perm => (
                                    <PermissionCheckbox
                                      key={perm.key}
                                      permission={perm}
                                      isActive={selectedUserPermissions.includes(perm.key)}
                                      onToggle={() => toggleUserPermission(perm.key)}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Special Permissions Section - Superadmin only */}
      {user?.role === 'superadmin' && (
        <Card data-testid="special-permissions-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5" />
                  Permisos Especiales
                </CardTitle>
                <CardDescription>
                  Operaciones sensibles del sistema. Configura que roles tienen acceso y si requieren autorizacion adicional de admin.
                </CardDescription>
              </div>
              <Button
                size="sm"
                onClick={saveDuplicateSettings}
                disabled={saving || !specialSettingsModified}
                data-testid="save-special-permissions"
              >
                <Save className="h-4 w-4 mr-1" />
                Guardar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="special-permissions-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-3 text-sm font-medium w-64">Operacion</th>
                    {['admin', 'supervisor', 'auditor', 'almacenista', 'user'].map(role => (
                      <th key={role} className="text-center py-3 px-2 text-sm font-medium">
                        {ROLE_LABELS[role]}
                      </th>
                    ))}
                    <th className="text-center py-3 px-3 text-sm font-medium">
                      <span className="flex items-center justify-center gap-1">
                        <Lock className="h-3 w-3" />
                        Auth Admin
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'duplicate_movements', label: 'Duplicar Movimientos', desc: 'Copiar un movimiento existente como nuevo', permission: 'duplicate_movements' },
                    { key: 'use_emergency_mode', label: 'Modo Emergencia', desc: 'Crear salidas sin verificacion de responsable', permission: 'use_emergency_mode' },
                    { key: 'use_barcode_scanner', label: 'Uso de QR / Scanner', desc: 'Usar escaner de codigos QR y barras', permission: 'use_barcode_scanner' },
                    { key: 'checkout_without_review', label: 'Salida sin Revision', desc: 'Omitir revision de equipo en salidas urgentes', permission: 'checkout_without_review' },
                    { key: 'approve_movements', label: 'Override Admin', desc: 'Aprobar movimientos como admin override', permission: 'approve_movements' },
                    { key: 'bulk_delete_inventory', label: 'Eliminacion Masiva (Inventario)', desc: 'Eliminar multiples equipos al mismo tiempo', permission: 'bulk_delete_inventory' },
                    { key: 'delete_movements', label: 'Eliminar Movimientos', desc: 'Borrar movimientos del historial', permission: 'delete_movements' },
                    { key: 'restore_movements', label: 'Restaurar Movimientos', desc: 'Recuperar movimientos eliminados', permission: 'restore_movements' },
                  ].map((op) => {
                    const opSettings = specialSettings[op.key] || {};
                    return (
                      <tr key={op.key} className="border-b hover:bg-muted/30 transition-colors" data-testid={`special-row-${op.key}`}>
                        <td className="py-3 px-3">
                          <p className="text-sm font-medium">{op.label}</p>
                          <p className="text-xs text-muted-foreground">{op.desc}</p>
                        </td>
                        {['admin', 'supervisor', 'auditor', 'almacenista', 'user'].map(role => {
                          const rolePerms = getRolePermissions(role);
                          const isActive = rolePerms.includes(op.permission);
                          return (
                            <td key={role} className="text-center py-3 px-2">
                              <Checkbox
                                checked={isActive}
                                onCheckedChange={() => toggleRolePermission(role, op.permission)}
                                className="h-5 w-5 mx-auto"
                                data-testid={`special-${op.key}-${role}`}
                              />
                            </td>
                          );
                        })}
                        <td className="text-center py-3 px-3">
                          <Checkbox
                            checked={opSettings.require_admin_auth || false}
                            onCheckedChange={(checked) => {
                              setSpecialSettings(prev => ({
                                ...prev,
                                [op.key]: { ...prev[op.key], require_admin_auth: !!checked }
                              }));
                              setSpecialSettingsModified(true);
                            }}
                            className="h-5 w-5 mx-auto"
                            data-testid={`special-auth-${op.key}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-muted/30 border text-xs text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <div>
                <strong>Roles por operacion:</strong> Los checks de cada rol modifican los permisos del rol directamente (igual que la seccion de arriba). 
                <strong className="ml-1">Auth Admin:</strong> Si esta activo, se pediran credenciales de admin/superadmin antes de ejecutar la operacion, independientemente del rol.
                <span className="ml-1">Recuerda guardar los cambios de rol con el boton de la seccion superior y los de Auth Admin con el boton de esta seccion.</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PermissionsPage;
